import React, { useEffect, useState } from 'react';
import { Select } from 'baseui/select';
import { useUserEstateStatuses, useUpdateUserEstateStatus } from '../hooks/useUserEstateStatus';
import type { UserEstate } from '../api/userEstate';
import { Spinner } from 'baseui/spinner';

interface UserEstateStatusSelectorProps {
  userEstate: UserEstate;
  disabled?: boolean;
}

export const UserEstateStatusSelector: React.FC<UserEstateStatusSelectorProps> = ({
  userEstate,
  disabled = false
}) => {
  const { statuses, isLoading: isLoadingStatuses, error } = useUserEstateStatuses();
  const updateStatusMutation = useUpdateUserEstateStatus();

  // Current status for the select
  const currentStatus = userEstate.user_estate_status;
  
  // Convert statuses to select options (BaseUI expects this format)
  const statusOptions = statuses.map(status => ({
    label: status.name,
    id: status.id.toString(), // Convert to string for BaseUI
    description: status.description
  }));

  const toOption = (status?: { id?: number; name?: string; description?: string } | null) =>
    status && status.id && status.name
      ? [{ label: status.name, id: status.id.toString(), description: status.description || '' }]
      : [];

  const [selectedValue, setSelectedValue] = useState(() => toOption(currentStatus));

  // The row is re-read after every save (see useUpdateUserEstateStatus.onSettled),
  // so whatever the server has now wins over the optimistic value below.
  useEffect(() => {
    setSelectedValue(toOption(currentStatus));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus?.id]);

  const handleStatusChange = async (params: any) => {
    if (params.type === 'select' && params.value.length > 0) {
      const newStatus = params.value[0];

      // Don't update if it's the same status
      if (parseInt(newStatus.id) === currentStatus?.id) {
        return;
      }

      // Show the choice right away; the refetch after the save corrects it if needed.
      setSelectedValue([{
        label: newStatus.label || '',
        id: newStatus.id || '',
        description: newStatus.description || ''
      }]);

      try {
        await updateStatusMutation.mutateAsync({
          user_id: userEstate.user_id,
          estate_id: userEstate.estate_id,
          status_id: parseInt(newStatus.id) // Convert back to number for API
        });
      } catch {
        // Feedback (toast) and the table refresh are handled by the hook.
        setSelectedValue(toOption(currentStatus));
      }
    }
  };

  if (isLoadingStatuses) {
    return <Spinner $size="24px" />;
  }

  if (statusOptions.length === 0) {
    return <div style={{ width: '140px', padding: '4px', fontSize: '11px' }}>No options</div>;
  }

  return (
    <div
      style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: '6px' }}
      title={updateStatusMutation.isPending ? 'Saving…' : undefined}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      {updateStatusMutation.isPending && <Spinner $size="14px" />}
      <Select
        options={statusOptions}
        value={selectedValue}
        onChange={handleStatusChange}
        disabled={disabled || updateStatusMutation.isPending}
        placeholder="Select status..."
        clearable={false}
        searchable={false}
        overrides={{
          Root: {
            style: {
              width: '100%',
              height: '100%',
              backgroundColor: 'transparent',
            }
          },
          ControlContainer: {
            style: {
              height: '100%',
              minHeight: 'auto',
              border: 'none',
              borderRadius: '0',
              backgroundColor: 'transparent',
              paddingLeft: '0',
              paddingRight: '0',
              ':hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
              ':focus': {
                backgroundColor: 'transparent',
                outline: 'none',
                boxShadow: 'none',
              },
            }
          },
          ValueContainer: {
            style: {
              height: '100%',
              padding: '0',
              fontSize: '12px',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: 'calc(100% - 20px)', // Leave space for arrow
            }
          },
          SingleValue: {
            style: {
              fontSize: '12px',
              color: '#374151',
              fontWeight: '500',
              margin: '0',
              padding: '0',
            }
          },
          SelectArrow: {
            style: {
              width: '16px',
              height: '16px',
              color: '#9CA3AF',
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
            }
          },
          Popover: {
            props: {
              mountNode: typeof document !== 'undefined' ? document.body : undefined,
              overrides: {
                Body: {
                  style: {
                    zIndex: 99999, // Ensure popover appears above everything
                  }
                }
              }
            }
          },
          Dropdown: {
            style: {
              zIndex: 99999, // Ensure dropdown appears above Mantine table and all other elements
              fontSize: '12px',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              backgroundColor: '#FFFFFF',
              marginTop: '2px',
            }
          }
        }}
      />
    </div>
  );
}; 